import {
  EditRCA,
  OperatingModeEnum,
  PossessionBaseEnum,
  RCAData,
  RequestRCA,
  VehicleType
} from '@insurance/api/requests'
import { deviceSession, formatDateToISOFormat } from '@shared/utils'
import { NamePath } from 'antd/es/form/interface'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

import { RcaFormValues } from './types'

export const RCA_FORM_DATA_KEY = '__rca_insurance_form_data__'

export const saveRcaValuesToStorage = (
  values: RcaFormValues,
  id: string | number = ''
) => {
  sessionStorage.setItem(id + RCA_FORM_DATA_KEY, JSON.stringify(values))
}

export const getRcaValuesFromStorage = (id: string | number = '') => {
  const values = sessionStorage.getItem(id + RCA_FORM_DATA_KEY)

  if (!values) return

  const parsedValues = JSON.parse(values)
  const { validFromProps, possessionBaseDocDateProps } = formProps()

  return {
    ...parsedValues,
    ...(parsedValues[validFromProps.name] && {
      [validFromProps.name]: dayjs(parsedValues[validFromProps.name])
    }),
    ...(parsedValues[possessionBaseDocDateProps.name] && {
      [possessionBaseDocDateProps.name]: dayjs(
        parsedValues[possessionBaseDocDateProps.name]
      )
    })
  }
}

export const removeRcaValuesFromStorage = (id: string | number = '') =>
  sessionStorage.removeItem(id + RCA_FORM_DATA_KEY)

export const formProps = (tFunction?: TFunction) => {
  const t = tFunction ?? (() => '')

  return {
    vehicleTypeProps: { name: 'vehicle_type', label: t('vehicle_type') },
    contractorTypeProps: {
      name: 'contractor_type',
      label: t('person_type')
    },
    identificationNumberProps: {
      name: 'identification_number',
      label: `${t('identification_number')} / ${t('fiscal_code')}`
    },
    certificateNumberProps: {
      name: 'certificate_number',
      label: t('certificate_number')
    },
    trailerCertificateNumberProps: {
      name: 'trailer_certificate_number',
      label: t('trailer_registration_certificate')
    },
    operatingModeProps: {
      name: 'operating_mode',
      label: t('operating_mode')
    },
    validFromProps: { name: 'valid_from', label: t('valid_from') },
    possessionBaseProps: {
      name: 'possession_base',
      label: t('possession_base')
    },
    possessionBaseDocNumberProps: {
      name: 'possession_base_doc_number',
      label: t('document_number')
    },
    possessionBaseDocDateProps: {
      name: 'possession_base_doc_date',
      label: t('document_date')
    },
    possessionBaseDocOtherTitleProps: {
      name: 'possession_base_doc_other_title',
      label: t('document_title')
    },
    possessionBaseDocNoteProps: {
      name: 'possession_base_doc_note',
      label: t('document_note')
    },
    vehicleDataProps: { name: 'vehicle_data' },
    contractorPersonProps: { name: 'contractor_person' },
    vehicleSelectedProps: { name: 'vehicle_selected' },
    trailerVehicleSelectedProps: { name: 'trailer_vehicle_selected' },
    customerProps: { name: 'customer', label: t('customer') },
    creditProps: {
      name: 'credit',
      label: t('amount_used_from_balance')
    },

    // Hidden
    stepKey: { name: 'step' },
    priceKey: { name: 'price' },
    orderKey: { name: 'order_id' },
    isPropertyKey: { name: 'is_property' },
    warningsKey: { name: 'underwriting_warnings' }
  } satisfies Record<string, { name: NamePath; label?: React.ReactNode }>
}

export const operatingModeLabels: Record<OperatingModeEnum, string> = {
  [OperatingModeEnum._7]: 'usual',
  [OperatingModeEnum._2]: 'minibus',
  [OperatingModeEnum._3]: 'intercity_bus',
  [OperatingModeEnum._4]: 'taxi',
  [OperatingModeEnum._5]: 'rent_a_car'
}

export const canProcureRca = (vehicleData?: RCAData): boolean => {
  if (!vehicleData?.expire) return true
  const monthsRemaining = dayjs(vehicleData.expire).diff(dayjs(), 'month', true)

  return monthsRemaining <= 2
}

export const normalizeRcaData = (values: RcaFormValues): RequestRCA => {
  const session = deviceSession()

  return {
    session: {
      device_id: session.deviceId,
      session_id: session.sessionId
    },
    identification_number: values.identification_number,
    certificate_number: values.certificate_number,
    ...(values.vehicle_type === VehicleType.TRAILER &&
      values.trailer_certificate_number && {
        trailer_certificate_number: values.trailer_certificate_number
      }),
    ...(values.operating_mode !== undefined && {
      operating_mode: values.operating_mode
    })
  } as RequestRCA
}

export const normalizeOrderData = (values: RcaFormValues) => {
  const rcaData = normalizeRcaData(values)

  return {
    ...rcaData,
    valid_from: formatDateToISOFormat(values.valid_from)!,
    ...(values.possession_base && {
      possession_base: values.possession_base,
      ...(values.possession_base !== PossessionBaseEnum.PROPERTY && {
        ...(values.possession_base_doc_number && {
          possession_base_doc_number: values.possession_base_doc_number
        }),
        ...(values.possession_base_doc_date && {
          possession_base_doc_date: formatDateToISOFormat(
            values.possession_base_doc_date
          )
        }),
        ...(values.possession_base_doc_other_title && {
          possession_base_doc_other_title:
            values.possession_base_doc_other_title
        }),
        ...(values.possession_base_doc_note && {
          possession_base_doc_note: values.possession_base_doc_note
        })
      })
    }),
    customer: values.customer
  } as EditRCA
}
